import { Component, TemplateRef, ElementRef, ViewChild, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, filter, finalize } from 'rxjs';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { AuthorizationService as AUTHORIZATION, BeUtil, FeUtil, TYPE } from '../../../hezky-routes';
import { getLocationTree, selectLocation } from '../../location-tree/state/location-tree.state';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-get-support',
  templateUrl: './get-support.component.html',
})

export class GetSupportComponent implements OnInit {
  @ViewChild('supportFile', { static: false }) supportFile!: ElementRef;
  @ViewChild('viewTicketModal', { static: false }) viewTicketModal!: TemplateRef<any>;
  paginationConfig: NzTableQueryParams = { pageIndex: 1, pageSize: 20, sort: [], filter: [] };
  isTableLoading:boolean = true;
  value: any[] = [];
  nodes: any[] = [];
  location_id!: any;
  isLocationError: boolean = false;
  fileData: any[] = [];
  fileList: File[] = [];
  location_ids?: any[] = [];
  maxFileSize = 5 * 1024 * 1024;
  maxFiles = 3;
  formSubmitted: boolean = false;
  isLoading: boolean = false;
  isDataValid = false;
  ticketForm!: FormGroup;
  quickFilter: string = 'all';
  recentTickets: any[] = [];
  filters: Array<any> = [
    {
      value: 'all',
      class: 'font-semibold radio-button-primary',
      icon: 'unordered-list',
      label: 'All',
    },
    {
      value: '-1',
      class: 'font-semibold radio-button-success',
      icon: 'check-circle',
      label: 'Closed',
    },
    {
      value: '1',
      class: 'font-semibold radio-button-danger',
      icon: 'close-circle',
      label: 'Open',
    },
  ];
  customer_id?: number;
  ticketList: any[] = [];
  ticketById: { subject: string; description: string; fileLocation: string } = {
    subject: '',
    description: '',
    fileLocation: '',
  };
  fullText = this.ticketById.description;
  truncatedText = this.fullText.substring(0, 50);
  isExpanded = false;
  parsedFileLocations: string[] = [];
  isAuthenticated: boolean = false;

  constructor(protected _location: Location,
    protected readonly feUtil: FeUtil,
    private readonly beUtil: BeUtil,
    public authorization: AUTHORIZATION) {
    this.isAuthenticated = this.authorization.isAuthenticated();
  }

  ngOnInit() {
    this.initializeForm();
    this.ticketList = this.recentTickets;
    if (this.isAuthenticated) {
      this.feUtil.store.select(getLocationTree).subscribe({
        next: (res) => {
          this.nodes = res ? JSON.parse(JSON.stringify(res)) : [];
          this.updateLocationNode();
        }
      })
      this.feUtil.store.select(selectLocation)
        .pipe(
          filter(location => location !== null),
          distinctUntilChanged(),
          untilDestroyed(this)
        ).subscribe(() => {
          setTimeout(() => this.getRecentTicket(), 0);
        });
      const isInit = localStorage.getItem('INIT') === 'true';
      if (isInit) { this.getRecentTicket() }
    }
  }

  private updateLocationNode() {
    if (!Array.isArray(this.nodes)) return;
    for (const node of this.nodes) {
      if (node.level === 0) {
        node.disabled = true;
        node.checked = false;
      }
    }
  }

  protected quickFilterChange(event: any) {
    this.ticketList = this.recentTickets.filter((ticket) => {
      if (event === 'all') {
        return ticket;
      }
      if (event === '-1') {
        return ticket.status === 'closed';
      }
      if (event === '1') {
        return ticket.status === 'open';
      }
    });
  }

  protected initializeForm() {
    if (this.authorization.isAuthenticated() === false) {
      this.ticketForm = this.feUtil.fb.group({
        customer_id: [null, [Validators.required]],
        user_name: [null],
        email: [null, [Validators.required, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)]],
        subject: [null, [Validators.required]],
        description: [null, [Validators.required, Validators.maxLength(500)]],
      });
    } else {
      this.ticketForm = this.feUtil.fb.group({
        user_subject: [null, [Validators.required]],
        user_description: [null, [Validators.required, Validators.maxLength(500)]],
      });
    }
  }

  protected onFileChange(event: any): void {
    const file = event.target.files;
    const newFiles = Array.from(file);
    if (this.fileList.length + newFiles.length > this.maxFiles) {
      this.feUtil.message.error(
        `You can only upload a maximum of ${this.maxFiles} files`
      );
      return;
    }
    for (const file of newFiles as File[]) {
      if (file.size > this.maxFileSize) {
        this.feUtil.message.error(`File ${file.name} exceeds 5MB limit`);
        continue;
      }
      const fileName = file.name.toLowerCase();
      const validExtensions = ['.pdf', '.jpeg', '.jpg', '.png', '.mp4'];
      const isValidType = validExtensions.some((ext) => fileName.endsWith(ext));
      if (!isValidType) {
        this.feUtil.message.error(`File ${file.name} is not an allowed file type. Please upload pdf, jpeg, png or mp4 files only.`);
        continue;
      }
      this.fileList.push(file);
    }
  }

  protected removeFile(index: number): void {
    this.fileList.splice(index, 1);
    this.supportFile.nativeElement.value = '';
  }

  protected getStatusColor(status: TYPE.TicketStatusType): string {
    /**
     * Colors associated with each ticket status.
     * - open: error
     * - in-progress: warning
     * - resolved: success
     * - closed: success
     */
    const colors = {
      open: 'error',
      'in-progress': 'warning',
      resolved: 'success',
      closed: 'success',
    };
    return colors[status] || 'default';
  }

  protected resetForm() {
    this.ticketForm.reset();
    this.fileList = [];
    if (this.authorization.isAuthenticated() === true) {
      this.supportFile.nativeElement.value = '';
      this.value = [];
      this.location_id = undefined;
      this.isLocationError = false;
    }
    this.isLoading = false;
  }

  protected onLocationChange(event:string) {
    if(!event) {
      this.isLocationError = true;
      return;
    }
    this.location_id = Number(event);
    this.isLocationError = false;
  }

  protected get isFormValid(): boolean {
    if (!this.ticketForm.valid) return false;
    if (this.isAuthenticated && (!this.value || this.value.length === 0 || !this.location_id)) return false;
    return true;
  }

  protected onSubmitTicket() {
    this.formSubmitted = true;
    this.isLoading = true;
    if (this.authorization.isAuthenticated() === false) {
      if (this.feUtil.validate(this.ticketForm)) {
        const payload = {
          Login_Ticket: {
            ...this.ticketForm.getRawValue(),
            status: 'open',
          }
        };
        this.beUtil.adminSvc.createTicket(payload)
          .pipe(untilDestroyed(this))
          .subscribe({
            next: (response: any) => {
              if (response.status === 200) {
                this.feUtil.modalSvc.success({
                  nzTitle: 'Success',
                  nzContent: `Your ticket has been submitted successfully!
                   Ticket ID: ${response.ticket_id}`,
                  nzOnOk: () => { },
                });
                this.ticketForm.reset();
              } else {
                this.feUtil.message.error('Failed to submit Ticket');
              }
              this.isLoading = false;
            },
            error: (err) => {
              this.isLoading = false
            }
          });
      }
    } else {
      if (!this.value || this.value.length === 0 || !this.location_id) {
        this.isLocationError = true;
        this.feUtil.message.error('Please select a location');
        this.isLoading = false;
        return;
      }
      if (this.feUtil.validate(this.ticketForm)) {
        const newTicket = {
          ...this.ticketForm.value,
          user_status: 'open',
          user_id: this.feUtil.user_data.user_id,
          user_customer_id: this.feUtil.user_data.customer.customer_id,
          customer_name: this.feUtil.user_data.customer.customer_name,
          user_email: this.feUtil.user_data.email,
          role_id: this.feUtil.user_data.userRoles.role_id,
          // user_name: this.feUtil.user_name,
          location_id: this.location_id,
        };
        const formData = new FormData();
        formData.append('Ticket', JSON.stringify(newTicket));
        this.fileList.forEach((value:any) => {
          formData.append('files', value, value.name);
        });
        this.beUtil.adminSvc.createTicket(formData)
          .pipe(untilDestroyed(this))
          .subscribe({
            next: (response: any) => {
              if (response.status === 200) {
                this.feUtil.modalSvc.success({
                  nzTitle: 'Success',
                  nzContent: `Your ticket has been submitted successfully!
                   Ticket ID:  ${response.ticket_id}`,
                  nzOnOk: () => {},
                });
                this.getRecentTicket();
                this.ticketForm.reset();
                this.fileList = [];
              }
              else if(response.status === 406) {
                const infectedFiles = response.error.infectedFiles;
                const infectedFilesList = infectedFiles
                  .map((file: any) => `<li>${file.name} - Viruses: ${file.viruses.join(', ')}</li>`)
                  .join('');
                  this.feUtil.modalSvc.error({
                  nzTitle: 'Virus Detected in Uploaded Files',
                  nzContent: `
                    <p>The following files are infected and cannot be uploaded:</p>
                    <ul>${infectedFilesList}</ul>
                  `,
                  nzOnOk: () => {},
                });
              }
              else {
                this.feUtil.message.error('Failed to submit Ticket');
              }
              this.isLoading = false;
            },
            error: (err) => this.isLoading = false,
          });
      }
    }
  }

  protected getRecentTicket() {
    this.isTableLoading = true;
    this.ticketList = [];
    this.beUtil.adminSvc.getTickets()
      .pipe(untilDestroyed(this), finalize(() => this.isTableLoading = false))
      .subscribe({
        next: ({ status, data }) => {
          if (status !== 200) return;
          this.recentTickets = data;
          this.ticketList = this.recentTickets;
          this.isTableLoading = false;
        },
      });
  }

  protected toggleText(): void {
    this.isExpanded = !this.isExpanded;
  }

  protected viewTicket(ticket: any): void {
    this.ticketById = ticket;
    this.parsedFileLocations = JSON.parse(ticket.fileLocation[0] == null ? '[]' : ticket.fileLocation);
    this.feUtil.modalSvc.create({
      nzTitle: 'Ticket Details for Ticket ID: ' + ticket.ticket_id,
      nzContent: this.viewTicketModal,
      nzFooter: null,
      nzClosable: true,
      nzCentered: true,
      nzCloseOnNavigation: true,
    });
  }

  protected openAttachment(fileUrl: string): void {
    if (!fileUrl) return;
    window.open(fileUrl, '_blank');
  }
}